import * as React from 'react';
import { cn } from '../lib/utils';

export interface PageHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Primary and secondary actions, trailing edge. */
  actions?: React.ReactNode;
  /** Rendered before the title — a back button, an avatar. */
  leading?: React.ReactNode;
  className?: string;
}

/**
 * The title band across the top of a page: a card-coloured strip, edge to edge
 * of the content area, with the title and description on the leading side and
 * the page's actions on the trailing side.
 *
 * Use it through `Page`, which also provides the padded body underneath.
 */
export function PageHeader({ title, description, actions, leading, className }: PageHeaderProps) {
  return (
    <div
      data-slot="page-header"
      className={cn(
        'flex flex-wrap items-center justify-between gap-x-6 gap-y-4 border-b bg-card px-4 py-6 md:px-6',
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-4">
        {leading}
        <div className="grid min-w-0 gap-1">
          <h1 className="truncate text-2xl leading-8 font-semibold tracking-tight">{title}</h1>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
      </div>

      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export interface PageProps extends Partial<PageHeaderProps> {
  children?: React.ReactNode;
  /** Classes for the padded body. */
  bodyClassName?: string;
}

/**
 * A full page: the header band (when a title is given) and the padded body the
 * page's cards sit in. Every routed screen renders exactly one.
 */
export function Page({ title, bodyClassName, children, ...header }: PageProps) {
  return (
    <>
      {title !== undefined ? <PageHeader title={title} {...header} /> : null}
      <div className={cn('flex flex-col gap-6 p-4 md:p-6', bodyClassName)}>{children}</div>
    </>
  );
}
